import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type Sender = "vozis" | "lead" | "stage";
interface Message { id: number; sender: Sender; text: string; }
interface Option { label: string; type?: "positive" | "objection"; action: () => void; }
interface LeadState { tamanho: string | null; dor: string | null; concorrente: string | null; status: "D1" | "D3" | null; }

let msgCounter = 0;
const nextId = () => ++msgCounter;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
function getNextWeekISO() {
  const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(10, 0, 0, 0);
  return d.toISOString().slice(0, 16).replace("T", " ");
}

// ─── PROTOTYPE COMPONENT ──────────────────────────────────────────────────────
function VozisPrototype() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [timerSec, setTimerSec] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [endData, setEndData] = useState<{ status: "D1" | "D3"; state: LeadState } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const leadState = useRef<LeadState>({ tamanho: null, dor: null, concorrente: null, status: null });

  useEffect(() => {
    if (!timerActive) return;
    const iv = setInterval(() => setTimerSec((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [timerActive]);

  const timerLabel = `${String(Math.floor(timerSec / 60)).padStart(2, "0")}:${String(timerSec % 60).padStart(2, "0")}`;

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping]);

  const addMessage = (sender: Sender, text: string) =>
    setMessages((prev) => [...prev, { id: nextId(), sender, text }]);

  const vozisTypeThenSay = async (text: string, ms = 1200) => {
    setOptions([]); setIsTyping(true); await sleep(ms); setIsTyping(false); addMessage("vozis", text);
  };
  const leadSays = async (text: string) => { setOptions([]); addMessage("lead", text); await sleep(350); };
  const addStage = (label: string) => addMessage("stage", label);

  const startFlow = async () => {
    setTimerActive(true); await sleep(400); addStage("saudação inicial");
    await vozisTypeThenSay("Oi <b>João</b>! Tá me ouvindo bem?", 700);
    setOptions([
      { label: "Sim, tô te ouvindo.", type: "positive", action: abertura },
      { label: "Quem fala?", action: async () => { await leadSays("Quem fala?"); await vozisTypeThenSay("Aqui é a Vozis, assistente de prospecção. Tá podendo falar por um minutinho?", 800); setOptions([{ label: "Pode falar.", type: "positive", action: abertura }, { label: "Não tenho tempo.", action: semTempo }]); } },
    ]);
  };

  const abertura = async () => {
    await leadSays("Sim, pode falar."); addStage("abertura");
    await vozisTypeThenSay("Aqui é a Vozis. Tenho uma novidade que pode te interessar bastante na área comercial. Tá podendo falar agora?", 1000);
    setOptions([{ label: "Sim, pode continuar.", type: "positive", action: apresentacao }, { label: "Não tenho tempo agora.", action: semTempo }]);
  };

  const semTempo = async () => {
    await leadSays("Não tenho tempo agora.");
    await vozisTypeThenSay("Entendo, João. Que tal uma conversa de 20 minutos na próxima semana? Sem compromisso algum.", 1000);
    setOptions([{ label: "Ok, pode continuar rapidinho.", type: "positive", action: apresentacao }, { label: "Me manda por e-mail.", action: async () => { await leadSays("Me manda por e-mail."); await vozisTypeThenSay("Claro! Mas o material sozinho não mostra o que a Vozis faz de diferente. É tipo ver foto de comida versus provar. Que tal 20 minutos pra ver funcionando na prática?", 1200); setOptions([{ label: "Ok, aceito.", type: "positive", action: apresentacao }, { label: "Não, obrigado.", action: encerrarNegativo }]); } }]);
  };

  const apresentacao = async () => {
    await leadSays("Ok, pode continuar."); addStage("apresentação do produto");
    await vozisTypeThenSay("A Vozis automatiza chamadas comerciais com IA — prospecção, follow-up, qualificação — com naturalidade de conversa humana. Você já ouviu falar de algo assim?", 1300);
    setOptions([
      { label: "Nunca ouvi.", action: async () => { await leadSays("Nunca ouvi."); await vozisTypeThenSay("Imagina sua equipe focada <b>só nas negociações quentes</b> enquanto a Vozis cuida de todo o volume. Faz sentido pro seu contexto?", 1100); showFazSentido(); } },
      { label: "Já vi algo parecido.", action: async () => { await leadSays("Já vi algo parecido."); await vozisTypeThenSay("Que bom! João, imagina sua equipe focada <b>só nas negociações quentes</b> enquanto a Vozis cuida do volume. Faz sentido?", 1000); showFazSentido(); } },
    ]);
  };

  const showFazSentido = () => setOptions([
    { label: "Sim, faz muito sentido!", type: "positive", action: perguntasChave },
    { label: "Já usamos outra ferramenta.", action: async () => { await leadSays("Já usamos outra ferramenta."); await vozisTypeThenSay("Que bom que vocês já pensam em automação! Qual ferramenta vocês usam?", 800); setOptions([{ label: "HubSpot.", action: async () => { leadState.current.concorrente = "HubSpot"; await leadSays("HubSpot."); await vozisTypeThenSay("Muitos clientes nossos vieram do HubSpot. O diferencial da Vozis está na <b>naturalidade da conversa</b> e na escuta ativa da IA. Que tal um comparativo rápido?", 1200); setOptions([{ label: "Sim, pode continuar.", type: "positive", action: perguntasChave }, { label: "Não, estamos satisfeitos.", action: encerrarNegativo }]); } }, { label: "RD Station.", action: async () => { leadState.current.concorrente = "RD Station"; await leadSays("RD Station."); await vozisTypeThenSay("Vários clientes nossos vieram do RD Station. O diferencial está na <b>naturalidade da conversa</b>. Vale um comparativo rápido?", 1200); setOptions([{ label: "Sim, pode continuar.", type: "positive", action: perguntasChave }, { label: "Não, obrigado.", action: encerrarNegativo }]); } }]); } },
    { label: "Nossa equipe já dá conta.", action: async () => { await leadSays("Nossa equipe já dá conta."); await vozisTypeThenSay("Que ótimo! A questão é: <em>e se vocês pudessem fazer o dobro sem contratar ninguém?</em> A Vozis potencializa — sua equipe foca nos leads quentes, a IA cuida do resto. Vale conhecer?", 1400); setOptions([{ label: "Faz sentido, pode continuar.", type: "positive", action: perguntasChave }, { label: "Não, obrigado.", action: encerrarNegativo }]); } },
  ]);

  const perguntasChave = async () => {
    await leadSays("Sim, faz sentido."); addStage("qualificação");
    await vozisTypeThenSay("Perfeito, João! Quantas pessoas fazem parte do time comercial atualmente?", 900);
    setOptions([
      { label: "2 a 5 pessoas", action: async () => { leadState.current.tamanho = "2-5 pessoas"; await leadSays("Somos 5 no comercial."); await perguntaDor(); } },
      { label: "6 a 15 pessoas", action: async () => { leadState.current.tamanho = "6-15 pessoas"; await leadSays("Somos 10 pessoas."); await perguntaDor(); } },
      { label: "Mais de 15", action: async () => { leadState.current.tamanho = "+15 pessoas"; await leadSays("Mais de 15 pessoas."); await perguntaDor(); } },
    ]);
  };

  const perguntaDor = async () => {
    await vozisTypeThenSay("E o maior desafio hoje seria a <b>falta de escala</b>, o <b>volume de leads</b> pra ligar, ou a <b>qualidade</b> dos que chegam pro closer?", 1000);
    setOptions([
      { label: "Falta de escala.", action: async () => { leadState.current.dor = "falta de escala"; await leadSays("Falta de escala."); await perguntaConcorrente(); } },
      { label: "Volume de leads.", action: async () => { leadState.current.dor = "volume de leads"; await leadSays("Volume de leads pra ligar."); await perguntaConcorrente(); } },
      { label: "Qualidade dos leads.", action: async () => { leadState.current.dor = "qualidade dos leads"; await leadSays("Qualidade dos leads."); await perguntaConcorrente(); } },
    ]);
  };

  const perguntaConcorrente = async () => {
    await vozisTypeThenSay("Faz sentido. Já testaram alguma ferramenta de automação parecida antes?", 900);
    setOptions([
      { label: "Não, nunca testamos.", action: async () => { if (!leadState.current.concorrente) leadState.current.concorrente = "nenhuma"; await leadSays("Nunca testamos."); await proximosPassos(); } },
      { label: "Sim, mas não funcionou bem.", action: async () => { await leadSays("Sim, mas os resultados foram fracos."); leadState.current.concorrente = "ferramenta anterior"; await proximosPassos(); } },
    ]);
  };

  const proximosPassos = async () => {
    addStage("próximos passos");
    await vozisTypeThenSay("Entendi! Pelo que me passou, faz muito sentido te mostrar a plataforma funcionando. Posso agendar um bate-papo de <b>20 minutos</b> com um dos nossos especialistas?", 1300);
    setOptions([
      { label: "Sim, pode agendar!", type: "positive", action: agendarReuniao },
      { label: "Qual o valor?", action: async () => { await leadSays("Qual o valor?"); await vozisTypeThenSay("O investimento varia conforme a necessidade — temos plano de entrada e outro pra escalar. O especialista apresenta as opções na reunião. Combinado?", 1200); setOptions([{ label: "Ok, vamos agendar.", type: "positive", action: agendarReuniao }, { label: "Prefiro saber antes.", action: encerrarNegativo }]); } },
      { label: "Não estou interessado agora.", action: async () => { await leadSays("Não no momento."); await vozisTypeThenSay("Entendo completamente. Mas empresas do seu setor estão reduzindo <b>30% do tempo</b> em tarefas repetitivas e aumentando <b>40% os contatos qualificados</b>. Não valeria 20 minutinhos?", 1300); setOptions([{ label: "Ok, 20 min tudo bem.", type: "positive", action: agendarReuniao }, { label: "Não, obrigado mesmo.", action: encerrarNegativo }]); } },
    ]);
  };

  const agendarReuniao = async () => {
    leadState.current.status = "D1"; await leadSays("Sim, pode agendar."); addStage("encerramento positivo");
    await vozisTypeThenSay("Muito obrigada pelo seu tempo, João! O convite já está no seu e-mail. Tenha um ótimo dia. Até mais!", 900);
    setTimerActive(false); await sleep(700); setEndData({ status: "D1", state: { ...leadState.current } });
  };

  const encerrarNegativo = async () => {
    leadState.current.status = "D3"; await leadSays("Não, obrigado.");
    await vozisTypeThenSay("Sem problemas, João! Ficamos à disposição. Tenha um ótimo dia. Até mais!", 800);
    setTimerActive(false); await sleep(700); setEndData({ status: "D3", state: { ...leadState.current } });
  };

  const restart = () => {
    msgCounter = 0; setMessages([]); setOptions([]); setIsTyping(false);
    setTimerSec(0); setTimerActive(false); setEndData(null);
    leadState.current = { tamanho: null, dor: null, concorrente: null, status: null };
  };

  return (
    <div className="bg-card rounded-3xl overflow-hidden border border-border shadow-premium w-full max-w-sm mx-auto">
      <div className="bg-primary/20 px-5 py-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-primary/30 flex items-center justify-center text-primary font-semibold text-lg flex-shrink-0">V</div>
        <div><p className="text-foreground font-medium text-sm leading-tight">Vozis IA</p><p className="text-primary text-xs mt-0.5">Assistente de prospecção ativa</p></div>
      </div>
      <div className="bg-primary/10 px-5 py-1.5 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />
        <span className="text-primary text-xs font-mono">{timerLabel}</span>
        <span className="ml-auto text-muted-foreground text-xs">em chamada</span>
      </div>
      <div ref={chatRef} className="h-72 overflow-y-auto px-4 py-3 flex flex-col gap-2.5 bg-background scroll-smooth">
        {messages.map((msg) => {
          if (msg.sender === "stage") return <div key={msg.id} className="self-center"><span className="text-xs text-muted-foreground bg-card border border-border px-3 py-1 rounded-full">{msg.text}</span></div>;
          if (msg.sender === "vozis") return <div key={msg.id} className="self-start max-w-[82%] bg-primary/15 text-foreground text-xs px-3.5 py-2.5 rounded-2xl rounded-bl-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: msg.text }} />;
          return <div key={msg.id} className="self-end max-w-[82%] bg-secondary text-foreground text-xs px-3.5 py-2.5 rounded-2xl rounded-br-sm leading-relaxed">{msg.text}</div>;
        })}
        {isTyping && <div className="self-start bg-primary/15 px-4 py-3 rounded-2xl rounded-bl-sm"><div className="flex gap-1.5">{[0,150,300].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div></div>}
      </div>
      {endData ? (
        <div className="px-4 py-4 bg-card border-t border-border">
          <div className="text-center mb-3">
            <div className="text-2xl mb-1">{endData.status === "D1" ? "📅" : "📋"}</div>
            <p className="text-foreground font-medium text-sm">{endData.status === "D1" ? "Reunião agendada!" : "Chamada encerrada"}</p>
            <p className="text-muted-foreground text-xs mt-1 leading-relaxed">{endData.status === "D1" ? "n8n dispara: Google Calendar + CRM atualizado." : "n8n dispara: CRM marca lead como perdido."}</p>
          </div>
          <div className="bg-background rounded-xl border border-border p-3 mb-3 font-mono text-[11px]">
            <p className="text-muted-foreground mb-1.5 font-sans text-[10px]">payload → n8n webhook</p>
            {[["nome_lead","João Silva"],["status_desfecho", endData.status === "D1" ? "D1 — Reunião agendada" : "D3 — Descarte"],[...(endData.status==="D1"?[["data_hora",getNextWeekISO()]]:[])],...(endData.status==="D1"?[]:[[]])  ,["equipe_tamanho", endData.state.tamanho||"—"],["dor_principal", endData.state.dor||"—"],["concorrente_atual", endData.state.concorrente||"—"],["canal","ligação"]].filter(r => r.length===2).map(([k,v])=>(
              <div key={k as string} className="flex gap-2 mb-1"><span className="text-muted-foreground min-w-[100px]">{k as string}</span><span className={k==="status_desfecho"?(endData.status==="D1"?"text-primary":"text-destructive"):"text-foreground"}>{v as string}</span></div>
            ))}
          </div>
          <button onClick={restart} className="w-full bg-primary hover:bg-primary/90 active:scale-95 text-primary-foreground text-xs font-medium py-2.5 rounded-xl transition-all">↻ Simular novamente</button>
        </div>
      ) : (
        <div className="px-4 py-3 bg-card border-t border-border min-h-[72px]">
          {messages.length === 0 && !isTyping ? (
            <button onClick={startFlow} className="w-full bg-primary hover:bg-primary/90 active:scale-95 text-primary-foreground font-medium text-xs py-3 rounded-xl transition-all">📞 Iniciar simulação de chamada</button>
          ) : options.length > 0 ? (
            <><p className="text-muted-foreground text-[10px] mb-1.5">Responda como o lead:</p><div className="flex flex-col gap-1.5">{options.map((opt, i) => (<button key={i} onClick={opt.action} className={`text-left text-xs px-3 py-2 rounded-xl border transition-all active:scale-[0.98] leading-snug min-h-[44px] ${opt.type==="positive"?"border-primary/50 text-primary hover:bg-primary/10":opt.type==="objection"?"border-destructive/50 text-destructive hover:bg-destructive/10":"border-border text-muted-foreground hover:bg-secondary"}`}>{opt.label}</button>))}</div></>
          ) : <div className="flex items-center justify-center h-10"><span className="text-muted-foreground text-xs">aguardando...</span></div>}
        </div>
      )}
    </div>
  );
}

// ─── CASE PAGE ────────────────────────────────────────────────────────────────
export default function VozisCasePT() {
  const navigate = useNavigate();

  const metrics = [
    { value: "300%", label: "mais contatos/dia", sub: "sem aumentar equipe" },
    { value: "↓40%", label: "tempo em tarefas repetitivas", sub: "por rep de vendas" },
    { value: "3×", label: "capacidade de escala", sub: "em 30 dias de uso" },
    { value: "24/7", label: "prospecção ativa", sub: "sem interrupções" },
  ];

  const timeline = [
    { phase: "Semana 1", title: "Integração e treino", desc: "A Vozis é conectada ao CRM e treinada com o roteiro comercial da empresa. Zero código, configuração conversacional." },
    { phase: "Semana 2–3", title: "Piloto em produção", desc: "A IA começa as primeiras ligações reais. Dados de conversão alimentam o painel de controle em tempo real." },
    { phase: "Semana 4+", title: "Escala contínua", desc: "Volume de contatos cresce automaticamente. Equipe humana foca 100% em fechar os leads quentes gerados pela IA." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* HERO */}
      <section className="gradient-hero pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="mb-8 hover:bg-primary/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar aos projetos
          </Button>

          <div className="flex items-center gap-2 mb-6">
            <Badge variant="secondary" className="bg-primary/10 text-primary">Automação Comercial</Badge>
            <Badge variant="secondary" className="bg-primary/10 text-primary">IA de Voz</Badge>
            <span className="text-xs text-muted-foreground">2024</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
            Como a Vozis multiplica{" "}
            <span className="text-primary">contatos qualificados</span> sem aumentar equipe
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Uma plataforma de IA que assume as ligações de prospecção, qualificação e follow-up — liberando o time humano para fechar. Este case documenta o sistema, as decisões de design conversacional e as métricas projetadas.
          </p>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="bg-secondary py-10 border-y border-border">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {metrics.map((m) => (
            <div key={m.value} className="text-center">
              <p className="text-3xl font-bold text-primary mb-1">{m.value}</p>
              <p className="text-xs text-foreground font-medium">{m.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{m.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6">

        {/* CONTEXTO */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">01 · Contexto</p>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-4">O gargalo que toda equipe comercial conhece</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Times de vendas gastam até <strong className="text-foreground">60% do seu tempo</strong> em tarefas que não geram receita diretamente: ligar para leads frios, fazer follow-up, qualificar contatos e atualizar CRM. O resultado é previsível — os melhores vendedores estão ocupados demais para vender.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                A Vozis foi desenhada para inverter essa equação. A IA assume o topo do funil com naturalidade humana, enquanto o time humano passa a atuar exclusivamente nos momentos que exigem inteligência relacional.
              </p>
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border">
              <p className="text-xs text-muted-foreground mb-4 font-medium">Distribuição de tempo atual (típico)</p>
              {[
                { label: "Ligações frias e follow-up", pct: 58, color: "bg-destructive/70" },
                { label: "Qualificação manual", pct: 18, color: "bg-yellow-500/70" },
                { label: "Negociação e fechamento", pct: 16, color: "bg-primary" },
                { label: "Atualização de CRM", pct: 8, color: "bg-muted-foreground/50" },
              ].map((b) => (
                <div key={b.label} className="mb-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{b.label}</span><span className="font-medium">{b.pct}%</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${b.color} rounded-full`} style={{ width: `${b.pct}%` }} /></div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground mt-4">Com Vozis: equipe humana foca nos 16% que geram receita</p>
            </div>
          </div>
        </section>

        {/* SOLUÇÃO */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">02 · A Solução</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">Design conversacional que soa humano</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: "🎯", title: "Roteiro inteligente", desc: "A IA segue um roteiro estruturado com desvios naturais para objeções, pausas e escuta ativa — não um script rígido." },
              { icon: "🔗", title: "Integração nativa", desc: "Conecta com HubSpot, Pipedrive e Google Calendar via n8n. Ao encerrar a chamada, o CRM já está atualizado." },
              { icon: "📊", title: "Dados de qualificação", desc: "Cada chamada captura tamanho da equipe, dor principal e concorrente atual — alimentando o funil automaticamente." },
            ].map((card) => (
              <div key={card.title} className="border border-border rounded-2xl p-5 hover:border-primary/40 hover:bg-primary/5 transition-all">
                <div className="text-2xl mb-3">{card.icon}</div>
                <h3 className="font-medium text-foreground mb-2 text-sm">{card.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Fluxo N8N */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <p className="text-xs text-muted-foreground mb-5 font-medium">Arquitetura de orquestração · n8n</p>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { label: "Ligação encerra", color: "bg-secondary text-foreground" },
                { label: "Webhook n8n", color: "bg-secondary text-foreground" },
                { label: "Switch D1/D2/D3", color: "bg-yellow-500/20 text-yellow-400" },
                { label: "D1 → Calendar + CRM", color: "bg-primary/20 text-primary" },
                { label: "D3 → CRM Perdido", color: "bg-destructive/20 text-destructive" },
                { label: "Sheets Dashboard", color: "bg-secondary text-foreground" },
              ].map((node, i, arr) => (
                <div key={node.label} className="flex items-center gap-2 flex-shrink-0">
                  <div className={`${node.color} text-xs px-3 py-2 rounded-xl text-center min-w-[100px]`}>{node.label}</div>
                  {i < arr.length - 1 && <span className="text-muted-foreground text-xs">→</span>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROTÓTIPO */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">03 · Protótipo interativo</p>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Experimente você mesmo</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Este protótipo reproduz o roteiro completo da Vozis. Você joga o papel do lead — pode responder positivamente ou levantar objeções reais como "já usamos outra ferramenta" ou "qual o valor?".
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Ao encerrar, o sistema mostra o <strong className="text-foreground">payload JSON</strong> exato que seria disparado ao n8n, já com os dados de qualificação coletados durante a conversa.
              </p>
              <div className="space-y-3">
                {["Teste o contorno de objeções", "Veja o D1 (agendado) ou D3 (descartado)", "Inspecione o payload gerado"].map((tip) => (
                  <div key={tip} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {tip}
                  </div>
                ))}
              </div>
            </div>
            <VozisPrototype />
          </div>
        </section>

        {/* IMPLEMENTAÇÃO */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">04 · Implementação</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">Da configuração à escala em 30 dias</h2>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div key={item.phase} className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-background border-2 border-primary flex items-center justify-center text-primary font-medium text-xs flex-shrink-0">{i + 1}</div>
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-border my-2" />}
                </div>
                <div className="pb-8">
                  <p className="text-xs text-primary font-medium mb-1">{item.phase}</p>
                  <h3 className="font-medium text-foreground mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PROJEÇÕES */}
        <section className="py-14 border-b border-border">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">05 · Projeções de impacto</p>
          <h2 className="text-2xl font-bold text-foreground mb-8">O que os números dizem</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-5">
              {[
                { before: "80 ligações/dia", after: "320 ligações/dia", label: "Volume de contatos", icon: "📞" },
                { before: "~6h de tarefas manuais/rep", after: "~2h", label: "Tempo em repetitivo", icon: "⏱" },
                { before: "Prospecção em horário comercial", after: "24 horas / 7 dias", label: "Janela de prospecção", icon: "🌐" },
                { before: "Dados de qualificação inconsistentes", after: "100% estruturados no CRM", label: "Qualidade dos dados", icon: "📊" },
              ].map((row) => (
                <div key={row.label} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">{row.icon}</span>
                    <p className="text-xs text-muted-foreground font-medium">{row.label}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground line-through">{row.before}</span>
                    <span className="text-muted-foreground text-xs">→</span>
                    <span className="text-sm font-medium text-primary">{row.after}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border flex flex-col justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-6">Projeção de ROI · 90 dias</p>
                <div className="text-5xl font-bold text-primary mb-2">3–5×</div>
                <p className="text-foreground text-sm mb-4">retorno sobre o investimento projetado</p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Baseado em: aumento de 300% no volume de contatos, redução de 40% no tempo operacional da equipe e taxa média de conversão mantida — sem contratações adicionais.
                </p>
              </div>
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-xs text-muted-foreground">* Projeções baseadas em benchmarks de plataformas de automação comercial com IA em empresas de 5–50 pessoas</p>
              </div>
            </div>
          </div>
        </section>

        {/* REFLEXÕES */}
        <section className="py-14">
          <p className="text-xs text-primary tracking-widest uppercase mb-4">06 · Reflexões de design</p>
          <h2 className="text-2xl font-bold text-foreground mb-6">O que aprendi projetando esse sistema</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Naturalidade > Eficiência", text: "A tentação é otimizar para velocidade. Mas conversas rígidas aumentam o desligamento prematuro. Pausas, conectores e validação emocional convertam mais do que scripts diretos." },
              { title: "Objeção não é rejeição", text: "Cada objeção ('qual o valor?', 'já usamos outra coisa') é um sinal de interesse. O design do roteiro trata objeções como bifurcações, não como fins de caminho." },
              { title: "Dados como subproduto", text: "A qualificação estruturada (tamanho, dor, concorrente) acontece naturalmente na conversa. O CRM é alimentado sem que nenhum humano precise digitar nada." },
              { title: "Escalabilidade assimétrica", text: "A IA escala linearmente — dobrar o volume não custa o dobro. Isso muda a matemática do crescimento comercial de forma estrutural, não incremental." },
            ].map((item) => (
              <div key={item.title} className="border-l-2 border-primary pl-5">
                <h3 className="font-medium text-foreground mb-2 text-sm">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
